// Add-member Drawer (docs/09 §4, UI-LAW §4c). A Drawer —
// not a Popover — because the flow is deep-linked via `?new=1` and embeds a
// paged team select with Load-more plus the datalist user picker. Fully
// controlled: field values live in the container (the typed UUID drives the
// container's `?ids=` resolution query) and reset when the drawer closes.

import type { SyntheticEvent } from "react";

import type { components } from "../../api/schema";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Button,
  Drawer,
  Inline,
  InlineError,
  Select,
  Spinner,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import { isUuid, userOptionLabel } from "../shared/userDirectory";

type MemberRole = components["schemas"]["MemberRole"];
type Team = components["schemas"]["TeamResponse"];
type User = components["schemas"]["UserResponse"];

export interface AddMemberDrawerProps {
  appUserId: string;
  // Blocking create failure — stays inline under the action row (§4a: a
  // toast is never the sole surface for a blocking error).
  createErrorMessage?: string;
  grantableRoles: MemberRole[];
  isCreatePending: boolean;
  isOpen: boolean;
  membershipRole: MemberRole;
  onAppUserIdChange: (value: string) => void;
  onDismiss: () => void;
  onMembershipRoleChange: (value: MemberRole) => void;
  onSubmit: () => void;
  onTeamIdChange: (value: string) => void;
  // User-picker directory failure (visible-users page or UUID resolution).
  pickerErrorMessage?: string;
  selectedTeamId: string;
  selectedUser?: User;
  teams: ListQueryResult<Team>;
  userOptions: User[];
}

export function AddMemberDrawer({
  appUserId,
  createErrorMessage,
  grantableRoles,
  isCreatePending,
  isOpen,
  membershipRole,
  onAppUserIdChange,
  onDismiss,
  onMembershipRoleChange,
  onSubmit,
  onTeamIdChange,
  pickerErrorMessage,
  selectedTeamId,
  selectedUser,
  teams,
  userOptions,
}: AddMemberDrawerProps) {
  // Create stays disabled until a team is chosen, the pasted/picked id is a
  // valid UUID, AND the directory resolved it to a visible user.
  const submitDisabled =
    isCreatePending ||
    selectedTeamId.length === 0 ||
    !isUuid(appUserId.trim()) ||
    selectedUser == null;

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit();
  }

  return (
    <Drawer ariaLabel="Add member" isOpen={isOpen} onDismiss={onDismiss}>
      <form onSubmit={handleSubmit}>
        <Stack gap={3}>
          <Select
            label="Team"
            required
            value={selectedTeamId}
            onChange={(event) => {
              onTeamIdChange(event.currentTarget.value);
            }}
          >
            <option value="">Choose team</option>
            {teams.items.map((team) => (
              <option key={team.id} value={team.id}>
                {team.display_name}
              </option>
            ))}
          </Select>
          {teams.isPending ? (
            <Inline gap={2}>
              <Spinner label="Loading teams" />
              <Text variant="muted">Loading teams...</Text>
            </Inline>
          ) : null}
          {teams.errorMessage ? (
            <InlineError>{teams.errorMessage}</InlineError>
          ) : null}
          {teams.hasNextPage ? (
            <Button
              disabled={teams.isFetchingNextPage}
              size="sm"
              type="button"
              variant="secondary"
              onClick={teams.fetchNextPage}
            >
              {teams.isFetchingNextPage ? "Loading..." : "Load more teams"}
            </Button>
          ) : null}
          <TextInput
            description={
              selectedUser
                ? userOptionLabel(selectedUser)
                : "Choose a visible user or paste their UUID."
            }
            label="User"
            list="membership-user-options"
            required
            value={appUserId}
            onChange={(event) => {
              onAppUserIdChange(event.currentTarget.value);
            }}
          />
          <datalist id="membership-user-options">
            {userOptions.map((user) => (
              <option key={user.id} value={user.id}>
                {userOptionLabel(user)}
              </option>
            ))}
          </datalist>
          {pickerErrorMessage ? (
            <InlineError>{pickerErrorMessage}</InlineError>
          ) : null}
          <Select
            label="Role"
            required
            value={membershipRole}
            onChange={(event) => {
              onMembershipRoleChange(event.currentTarget.value as MemberRole);
            }}
          >
            {grantableRoles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </Select>
          <Inline gap={3}>
            <Button disabled={submitDisabled} type="submit">
              {isCreatePending ? "Adding..." : "Add member"}
            </Button>
            <Button type="button" variant="secondary" onClick={onDismiss}>
              Cancel
            </Button>
          </Inline>
          {createErrorMessage ? (
            <InlineError>{createErrorMessage}</InlineError>
          ) : null}
        </Stack>
      </form>
    </Drawer>
  );
}
